import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Flex } from 'antd';
import { Text } from 'components/Base/Typography';
import { Popover } from 'components/Base/Popover';
import { useGetPeopleIdentityQuery } from 'redux/apiSlice/people.slice';
import Spinner from 'components/Base/Spinner';
import { TPerson } from './TaskAsigneeDropdown.types';
import {
  StyledSearchInput,
  StyledPersonItem,
  StyledAssigneeButton,
} from './TaskAsigneeDropdown.styles';
import {
  AddPersonOutline,
  SearchOutline,
} from '../../../../../../../assets/icon/icon';

interface TTaskAssigneeDropdownProps {
  value?: string;
  disabled?: boolean;
  onAssigneeChange?: (assigneeId: string) => void;
}

const TaskAssigneeDropdown: React.FC<TTaskAssigneeDropdownProps> = ({
  value,
  disabled = false,
  onAssigneeChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(value);

  const { data: peopleIdentityOptions = [], isLoading } =
    useGetPeopleIdentityQuery();

  // Update internal state when prop changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Reset search when popover closes
  useEffect(() => {
    if (!isPopoverOpen) {
      setSearchTerm('');
    }
  }, [isPopoverOpen]);

  // Filter assignees based on search term - debounced filtering
  const filteredAssignees = useMemo(() => {
    if (!searchTerm.trim()) return peopleIdentityOptions;

    const searchLower = searchTerm.toLowerCase().trim();
    return peopleIdentityOptions.filter(person =>
      person.label.toLowerCase().includes(searchLower),
    );
  }, [searchTerm, peopleIdentityOptions]);

  // Memoized handlers to prevent unnecessary re-renders
  const handlePersonSelect = useCallback(
    (person: TPerson) => {
      if (person.value === internalValue) return;

      setInternalValue(person.label);
      setIsPopoverOpen(false);
      onAssigneeChange?.(person.value);
    },
    [internalValue, onAssigneeChange],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [],
  );

  const handlePopoverToggle = useCallback(() => {
    if (disabled) return;
    setIsPopoverOpen(prev => !prev);
  }, [disabled]);

  const handleSearchClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Render content based on state
  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex align="center" justify="center" style={{ height: 100 }}>
          <Spinner size="large" />
        </Flex>
      );
    }

    if (filteredAssignees.length === 0) {
      return (
        <Flex align="center" justify="center" style={{ padding: 16 }}>
          <Text>No person found</Text>
        </Flex>
      );
    }

    return filteredAssignees.map(person => (
      <StyledPersonItem
        key={person.value}
        onClick={() => handlePersonSelect(person)}
      >
        {person.label}
      </StyledPersonItem>
    ));
  };

  // Memoized popover content
  const popoverContent = useMemo(
    () => (
      <Flex vertical gap={8} style={{ width: 240 }}>
        <StyledSearchInput
          placeholder="Search"
          value={searchTerm}
          onChange={handleSearchChange}
          autoFocus
          onClick={handleSearchClick}
          prefix={<SearchOutline fill="#656565" size={18} />}
          style={{
            cursor: isLoading ? 'not-allowed' : '',
          }}
        />
        <Flex
          vertical
          style={{
            maxHeight: 224,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {renderContent()}
        </Flex>
      </Flex>
    ),
    [searchTerm, handleSearchChange, handleSearchClick, renderContent],
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={isPopoverOpen}
      onOpenChange={handlePopoverToggle}
      placement="bottomLeft"
      arrow={false}
      data-cy="task-assignee-popover"
    >
      <StyledAssigneeButton
        variant="ghost"
        icon={!internalValue ? <AddPersonOutline size={22} /> : null}
        style={{
          color: !internalValue ? '#808080' : 'inherit',
          cursor: disabled ? 'not-allowed' : '',
        }}
        data-cy="task-assignee-button"
      >
        {internalValue || 'Add'}
      </StyledAssigneeButton>
    </Popover>
  );
};

export default TaskAssigneeDropdown;
